/**
 * WebSocket Token Manager
 * Extracted token management logic for testability
 */

import { buildApiUrl } from './api-base';

export interface CachedToken {
  token: string;
  expiresAt: number; // Unix timestamp
  fetchedAt: number; // Unix timestamp
}

export interface TokenManagerOptions {
  getTokenOverride?: () => Promise<{ token: string; expiresAt: number }>;
  minFetchInterval?: number; // Minimum time between fetches in ms
  refreshBufferMs?: number;   // How long before expiry to refresh
}

/**
 * Token Manager class for WebSocket authentication
 */
export class TokenManager {
  private cachedToken: CachedToken | null = null;
  private inFlightPromise: Promise<CachedToken> | null = null;
  private readonly minFetchInterval: number;
  private readonly refreshBufferMs: number;
  private getTokenOverride?: () => Promise<{ token: string; expiresAt: number }>;

  constructor(options: TokenManagerOptions = {}) {
    this.minFetchInterval = options.minFetchInterval ?? 5000; // 5 seconds
    this.refreshBufferMs = options.refreshBufferMs ?? 60000;   // 60 seconds
    this.getTokenOverride = options.getTokenOverride;
  }

  /**
   * Fetch WebSocket token with singleflight pattern
   */
  async fetchToken(): Promise<CachedToken> {
    const now = Date.now();

    // Check if cached token is still valid
    if (this.cachedToken) {
      const timeUntilExpiry = this.cachedToken.expiresAt - now;
      const timeSinceLastFetch = now - this.cachedToken.fetchedAt;

      // Use cached token if:
      // 1. More than refreshBufferMs until expiry AND
      // 2. Less than minFetchInterval since last fetch (rate limiting)
      if (timeUntilExpiry > this.refreshBufferMs && timeSinceLastFetch < this.minFetchInterval) {
        console.log('🎫 Using cached WebSocket token', {
          timeUntilExpiry: Math.round(timeUntilExpiry / 1000),
          timeSinceLastFetch: Math.round(timeSinceLastFetch / 1000)
        });
        return this.cachedToken;
      }
    }

    // Singleflight: if already fetching, wait for existing fetch
    if (this.inFlightPromise) {
      console.log('🎫 Reusing in-flight token fetch');
      return this.inFlightPromise;
    }

    // Start new fetch with singleflight protection
    this.inFlightPromise = this.doFetchToken();

    try {
      const token = await this.inFlightPromise;
      return token;
    } finally {
      this.inFlightPromise = null;
    }
  }

  /**
   * Internal token fetching implementation
   */
  private async doFetchToken(): Promise<CachedToken> {
    console.log('🎫 Fetching new WebSocket token...');

    // Use demo override if provided
    if (this.getTokenOverride) {
      console.log('🎭 Using demo token override');
      const tokenData = await this.getTokenOverride();
      const now = Date.now();
      this.cachedToken = {
        token: tokenData.token,
        expiresAt: tokenData.expiresAt,
        fetchedAt: now,
      };
      return this.cachedToken;
    }

    const response = await fetch(buildApiUrl('/api/auth/ws-token'), {
      method: 'GET',
      credentials: 'include', // Cookie-based auth
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch WebSocket token: ${response.status}`);
    }

    const data = await response.json();

    if (!data.token || !data.expiresAt) {
      throw new Error('Invalid WebSocket token response');
    }

    // Parse expiry (assume ISO string)
    const expiresAt = new Date(data.expiresAt).getTime();
    const now = Date.now();

    this.cachedToken = {
      token: data.token,
      expiresAt,
      fetchedAt: now,
    };

    console.log('✅ WebSocket token cached', {
      expiresIn: Math.round((expiresAt - now) / 1000),
    });

    return this.cachedToken;
  }

  /**
   * Force token refresh (clears cache and fetches new token)
   */
  async forceReauth(): Promise<CachedToken> {
    console.log('🔄 Forcing token refresh...');
    this.clearCache();
    return this.fetchToken();
  }

  /**
   * Clear cached token and in-flight requests
   */
  clearCache(): void {
    console.log('🧹 Clearing token cache');
    this.cachedToken = null;
    this.inFlightPromise = null;
  }

  /**
   * Get current cached token without fetching
   */
  getCachedToken(): CachedToken | null {
    return this.cachedToken;
  }

  /**
   * Get time until expiry for current cached token
   */
  getTimeUntilExpiry(): number | null {
    if (!this.cachedToken) return null;
    return this.cachedToken.expiresAt - Date.now();
  }

  /**
   * Check if token should be refreshed
   */
  shouldRefresh(bufferMs: number = this.refreshBufferMs): boolean {
    if (!this.cachedToken) return true;
    const timeUntilExpiry = this.getTimeUntilExpiry();
    return timeUntilExpiry === null || timeUntilExpiry <= bufferMs;
  }

  /**
   * Update token override for demo/testing purposes
   */
  setTokenOverride(override?: () => Promise<{ token: string; expiresAt: number }>): void {
    this.getTokenOverride = override;
  }
}

/**
 * Calculate reconnection delay with exponential backoff + jitter
 */
export function calculateReconnectDelay(attempts: number): number {
  // Sequence: 1s, 2s, 4s, 8s, capped at 30s
  const baseDelay = Math.min(1000 * Math.pow(2, attempts), 30000);

  // Add ±20% jitter
  const jitter = baseDelay * 0.2 * (Math.random() * 2 - 1);

  return Math.max(baseDelay + jitter, 1000);
}

/**
 * Default token manager instance
 * Can be replaced for testing
 */
export let defaultTokenManager = new TokenManager();

/**
 * Set global token manager for testing
 */
export function setDefaultTokenManager(tokenManager: TokenManager): void {
  defaultTokenManager = tokenManager;
}

/**
 * Get current default token manager
 */
export function getDefaultTokenManager(): TokenManager {
  return defaultTokenManager;
}